/* =============================================================================
   VIEWS / ADMIN / DASHBOARD-TIMELINE — „Als Nächstes"-Pane (Timeline + Anfragen)
   =============================================================================
   S202 (REF-8) — aus dashboard.js extrahiert. Die kombinierte „Als Nächstes ⇄
   Anfragen"-Karte samt pure Timeline-Logik, Live-Pills und 60-Sekunden-Tick:
     - buildTodayTimeline, timelineStage, _orderStage, countTodayDone (pure,
       Status-Prädikate via _TIMELINE_DEPS injiziert — Single-Source, DI-testbar)
     - _normTimelineTime, _nowMinutes, _relTime, _renderTimelineRow
     - _nextPaneView (Modul-State: Toggle überlebt Re-Render)
     - renderNextPane, dashboardSetNextView
     - _timelineTick / ensureTimelineTick (kein Doppel-Interval)

   Reine Extraktion, NULL Verhaltensänderung. Der Fallback-Rück-Ruf auf ein
   volles renderDashboard() läuft über requestDashboardRerender (data) — kein
   Import des Orchestrators (acyclisch). Die „Anfragen"-Sicht liefert
   dashboard-lists (renderRequestsBody). Inline-Handler bleiben `func()`.
============================================================================ */

import { htmlEscape, todayISO, timeToMinutes } from '../../utils/helpers.js';
import { isOrderFinished, isOrderCashedOut, isOrderPaid, isOrderSettled } from '../../core/order-statuses.js';
import { isReservationDone } from '../../core/capacity.js';
import { getState, requestDashboardRerender } from './dashboard-data.js';
import { renderRequestsBody } from './dashboard-lists.js';

// — S82-6: Heute-Timeline — vollständige chronologische Tagesübersicht ————————
// Befund Jonas (S82): Der „Heute"-Tab zeigte nur Kacheln/Statistiken + eine
// reservierungs-only Liste. Diese Timeline listet ALLE Vorgänge des Tages
// (Reservierungen + Bestellungen aller Service-Typen) chronologisch, mit
// Service-Icon, Status-Pille und Click-to-Detail (Tab-Switch).

const TIMELINE_ICONS = { 'dine-in': '🍽️', takeaway: '🥡', delivery: '🛵', reservation: '📅' };
const TIMELINE_TYPE_LABEL = { 'dine-in': 'Hier Essen', takeaway: 'Abholung', delivery: 'Lieferung', reservation: 'Reservierung' };

function _normTimelineTime(t) {
  return String(t || '').slice(0, 5);
}

/**
 * Pure: baut die chronologische „Als Nächstes"-Liste — NUR noch offene Vorgänge.
 * Status-Prädikate werden INJIZIERT (deps), damit die Single-Source aus
 * `order-statuses.js` / `capacity.js` erhalten bleibt (Counter-Single-Source-
 * Regel) und der Helper vollständig testbar ist.
 *   deps.isOrderFinished(o) → bool  (picked_up/cancelled/no_show)
 *   deps.isOrderCashedOut(o) → bool (picked_up UND bezahlt = kassiert)
 *   deps.isOrderPaid(o) → bool      (payment_status = paid)
 *   deps.reservationDone(r) → bool  (cancelled/declined/no-show/waitlist/completed)
 *
 * S97: Erledigtes/Storniertes verschwindet. Eine Bestellung fällt raus, wenn
 * sie abgeschlossen ist (`isOrderFinished`), ein Storno-/Korrekturbeleg ist
 * (`isCorrection` — ein Dokument, keine Aufgabe) oder ihr Original storniert/
 * erstattet wurde (`correctedByOrderId`). Eine Reservierung fällt raus, wenn
 * `reservationDone`. Sortierung aufsteigend nach Uhrzeit → die Liste beginnt
 * automatisch bei der nächsten/aktuellen Aufgabe (überfällig-aber-offen oben).
 *
 * S115 — Reservierungs-Lifecycle-Kopplung: Die verknüpfte Order wird aus ALLEN
 * heutigen Orders ermittelt (nicht aus dem `!isFinished`-gefilterten `orders`),
 * sonst fällt eine `picked_up`-unbezahlte Order („Kassieren") schon raus und die
 * Reservierungs-Zeile verlöre Badge + Live-Status. Decision-Table:
 *   kassiert (picked_up & paid)        → Reservierung VERSCHWINDET (Aufgabe komplett)
 *   in Arbeit / picked_up-unbezahlt    → Reservierungs-Zeile + Badge + Order-Live-Status
 *   storniert / no_show                → schlichte Reservierungs-Zeile (Tisch steht)
 *   keine Order                        → schlichte Reservierungs-Zeile
 */
/**
 * S240-HF9 — Ist der VORGANG hinter einer Order abgearbeitet (Zeile darf aus
 * „Als Nächstes" verschwinden)? Pure — Vitest.
 *
 * BEFUND: die bezahlte Dine-In-Order 2026-08-26-001 blieb als „Angenommen"
 * stehen, obwohl Reservierung `completed` und Belegung beendet waren. Grund:
 * eine Dine-In-Order erreicht NIE `picked_up` — sie bleibt `confirmed` und wird
 * nur bezahlt. `isOrderFinished` (Status-Set) griff also nicht, und die Order
 * fiel in den Fallback „eigene Zeile".
 *
 * JONAS-REGEL: abgearbeitet = **bezahlt/storniert UND kein Tisch mehr besetzt**.
 * Solange die Check-in-/Walk-In-Belegung aktiv ist, bleibt die Zeile stehen —
 * die Gäste sitzen ja noch da, auch wenn schon kassiert wurde.
 *
 * WICHTIG (bewusst NICHT geändert): Bezahlung beendet die Belegung NIE
 * automatisch. Das macht nur die Tisch-Freigabe („Tisch jetzt freigeben?" im
 * Bon-Modal), das Belegungs-Modal oder der No-Show-Trigger. Diese Funktion
 * LIEST den Belegungs-Status, sie ändert ihn nicht.
 *
 * @param {object} order
 * @param {Array} occupancies  `state.tableOccupancies`
 * @param {{isOrderPaid?: Function}} [deps]
 */
export function isTimelineOrderDone(order, occupancies, deps = {}) {
  if (!order) return false;
  const paid = (typeof deps.isOrderPaid === 'function') ? deps.isOrderPaid(order) : false;
  const status = order.status;
  const settled = paid || status === 'cancelled' || status === 'no_show';
  if (!settled) return false;
  // Hängt eine AKTIVE Belegung dran → Tisch noch besetzt, Zeile bleibt.
  if (order.occupancyId) {
    const occ = (occupancies || []).find(o => o && o.id === order.occupancyId);
    if (occ && occ.status === 'active' && !occ.deletedAt) return false;
  }
  return true;
}

export function buildTodayTimeline(state, today, deps = {}) {
  const isFinished = typeof deps.isOrderFinished === 'function' ? deps.isOrderFinished : () => false;
  const isCashedOut = typeof deps.isOrderCashedOut === 'function' ? deps.isOrderCashedOut : () => false;
  const isPaid = typeof deps.isOrderPaid === 'function' ? deps.isOrderPaid : () => false;
  const isResDone  = typeof deps.reservationDone === 'function' ? deps.reservationDone : () => false;
  const reservations = (state.reservations || []).filter(r => r.date === today && !isResDone(r));
  const orders = (state.orders || []).filter(o =>
    o.pickupDate === today && !isFinished(o) && !o.isCorrection && !o.correctedByOrderId
    // S240-HF9: bezahlt/storniert UND kein Tisch mehr besetzt → Vorgang erledigt.
    // Fängt die Dine-In-Order, die nie `picked_up` wird (bleibt `confirmed`).
    && !isTimelineOrderDone(o, state.tableOccupancies, { isOrderPaid: isPaid })
  );
  // S115 — komplette heutige Orders (auch picked_up-unbezahlt = „Kassieren") für
  // den Reservierungs-Lookup; Storno-/Korrekturbelege ausgeschlossen (kein Vorgang).
  const allTodayOrders = (state.orders || []).filter(o =>
    o.pickupDate === today && !o.isCorrection && !o.correctedByOrderId
  );
  // S113 — Reservierung + ihre verknüpfte Order zu EINER Zeile zusammenführen:
  // Orders, die über `reservationId` zu einer heute gelisteten Reservierung gehören,
  // bekommen KEINE eigene Zeile, sondern hängen an der Reservierungs-Entry (Badge +
  // Live-Status der Order). Greift gleichermaßen für Online-Vorbestellung und
  // „Bestellung aufnehmen"/post-confirm „Ja" (beide erzeugen dieselbe verknüpfte Order).
  const resIds = new Set(reservations.map(r => r.id));
  const entries = [];
  for (const r of reservations) {
    const linked = allTodayOrders.find(o => o.reservationId === r.id);
    // S115 — kassiert → Aufgabe komplett, Reservierung verschwindet (zählt via
    // countTodayDone über den Order-Zweig).
    if (linked && isCashedOut(linked)) continue;
    // „lebende" Order = in Arbeit (pending..ready) ODER picked_up-unbezahlt
    // („Kassieren"). storniert/no_show ist KEIN laufender Vorgang mehr → die
    // Reservierung fällt auf die schlichte Zeile zurück (kein Badge).
    const showOrder = !!linked && linked.status !== 'cancelled' && linked.status !== 'no_show';
    entries.push({
      kind: 'reservation',
      icon: TIMELINE_ICONS.reservation,
      typeLabel: TIMELINE_TYPE_LABEL.reservation,
      time: _normTimelineTime(r.time),
      title: r.name || 'Reservierung',
      detail: (r.guests != null ? r.guests + ' Gäste' : ''),
      status: r.status || '',
      tab: 'reservations',
      id: r.id,
      hasOrder: showOrder,
      orderId: showOrder ? linked.id : null,
      orderNumber: showOrder ? (linked.orderNumber || '') : '',
      orderStatus: showOrder ? (linked.status || '') : '',
      kitchenStatus: showOrder ? (linked.kitchenStatus || '') : '',
      barStatus: showOrder ? (linked.barStatus || '') : '',
      orderPaid: showOrder ? isPaid(linked) : false,
    });
  }
  for (const o of orders) {
    // Verknüpfte Order einer gelisteten Reservierung → keine Doppelzeile (hängt
    // an der Reservierungs-Entry). Orders ohne Reservierung bzw. zu nicht
    // gelisteten Reservierungen (Vortag/erledigt) bleiben eigene Zeilen (Fallback).
    if (o.reservationId && resIds.has(o.reservationId)) continue;
    const svc = o.serviceType || 'takeaway';
    const kind = (svc === 'dine-in' || svc === 'delivery') ? svc : 'takeaway';
    entries.push({
      kind,
      icon: TIMELINE_ICONS[kind],
      typeLabel: TIMELINE_TYPE_LABEL[kind],
      time: _normTimelineTime(o.pickupTime),
      title: o.orderNumber ? ('#' + o.orderNumber) : (o.customerName || 'Bestellung'),
      detail: o.orderNumber ? (o.customerName || '') : '',
      status: o.status || '',
      kitchenStatus: o.kitchenStatus || '',
      barStatus: o.barStatus || '',
      deliveryStatus: o.deliveryStatus || '',
      serviceType: svc,
      tab: 'orders',
      id: o.id,
    });
  }
  return entries.sort((a, b) => {
    const ta = a.time || '99:99', tb = b.time || '99:99';
    if (ta !== tb) return ta.localeCompare(tb);
    return a.kind.localeCompare(b.kind);
  });
}

/**
 * Pure: Bearbeitungsstufe (Live-Status) für eine Timeline-Zeile. Liefert
 * `{ label, state, accent, overdue }` mit `state ∈ {'action','live','wait'}`:
 *   action = ICH muss etwas tun (amber Pill + „→ Verb")
 *   live   = läuft autonom (blaue Pill + Puls)
 *   wait   = angenommen/bestätigt, nichts zu tun (graue Pill)
 * Präzedenz bei Orders: `out_for_delivery` (live) vor `ready`-Aktion, sonst
 * `status`-basiert. `overdue` = Zeit < jetzt (und per Definition noch offen),
 * unabhängig vom state → roter Zeit-Zusatz. `nowMin` = Minuten seit Mitternacht.
 */
export function timelineStage(entry, nowMin = null) {
  const overdue = (() => {
    if (nowMin == null || !entry || !entry.time) return false;
    const m = timeToMinutes(entry.time);
    return Number.isFinite(m) && m < nowMin;
  })();
  const wrap = (label, state, accent) => ({ label, state, accent, overdue });
  if (!entry) return wrap('—', 'wait', 'grau');

  // S113 — Reservierung mit verknüpfter Order: Pill aus dem ORDER-Status (echter
  // Bestell-Fortschritt) berechnen, denselben Mapping wie der Order-Zweig.
  if (entry.kind === 'reservation' && entry.hasOrder) {
    return _orderStage(wrap, {
      status: entry.orderStatus,
      kitchenStatus: entry.kitchenStatus,
      barStatus: entry.barStatus,
      serviceType: 'dine-in',
      deliveryStatus: '',
      orderPaid: entry.orderPaid
    });
  }
  if (entry.kind === 'reservation') {
    if (entry.status === 'pending') return wrap('Anfrage → bestätigen', 'action', 'amber');
    return wrap('Bestätigt', 'wait', 'grau');
  }

  return _orderStage(wrap, entry);
}

/** Pure: Order-Status → Live-Pill (geteilt von Order-Zeilen UND Reservierungs-
 *  Zeilen mit verknüpfter Order, S113). Präzedenz: out_for_delivery vor ready. */
function _orderStage(wrap, entry) {
  const isDelivery = entry.serviceType === 'delivery';
  // 1) Fahrer unterwegs hat Vorrang vor jeder ready-Aktion.
  if (isDelivery && entry.deliveryStatus === 'out_for_delivery') {
    return wrap('Unterwegs', 'live', 'blau');
  }
  // S115 — picked_up + unbezahlt → offene Kasse („Kassieren"). Nur erreichbar
  // solange nicht kassiert (kassiert = Reservierung/Order ist eh schon raus).
  if (entry.status === 'picked_up' && !entry.orderPaid) {
    return wrap('Kassieren → kassieren', 'action', 'amber');
  }
  // 2) ready → Bediener-Aktion (ausgeben bzw. Fahrer zuweisen).
  if (entry.status === 'ready') {
    if (isDelivery) return wrap('Bereit → Fahrer zuweisen', 'action', 'amber');
    return wrap('Abholbereit → ausgeben', 'action', 'amber');
  }
  // 3) neu → annehmen.
  if (entry.status === 'pending') return wrap('Neu → annehmen', 'action', 'amber');
  // 4) explizit preparing.
  if (entry.status === 'preparing') return wrap('In Zubereitung', 'live', 'blau');
  // 5) confirmed: läuft KDS/Bar schon? → live, sonst angenommen.
  if (entry.status === 'confirmed') {
    if (entry.kitchenStatus === 'preparing' || entry.barStatus === 'preparing') {
      return wrap('In Zubereitung', 'live', 'blau');
    }
    return wrap('Angenommen', 'wait', 'grau');
  }
  return wrap('Angenommen', 'wait', 'grau');
}

/** Pure: Zählt die heute bereits erledigten Vorgänge (nur Zahl für den
 *  Header-Zähler). Storno-/Korrekturbelege zählen nicht als Aufgabe. */
export function countTodayDone(state, today, deps = {}) {
  // S115 — „erledigt" = isOrderSettled (kassiert ODER storniert/no_show), NICHT
  // isOrderFinished: picked_up-unbezahlt steht noch in „Kassieren" und darf nicht
  // vorzeitig zählen. So zählt die kassierte Reservierungs-Order exakt EINMAL
  // (Order-Zweig), die Reservierung selbst nicht doppelt (reservationDone=false).
  const isSettled  = typeof deps.isOrderSettled === 'function' ? deps.isOrderSettled : () => false;
  const isResDone  = typeof deps.reservationDone === 'function' ? deps.reservationDone : () => false;
  let n = 0;
  const settledResIds = new Set();                       // S117
  for (const o of (state.orders || [])) {
    if (o.pickupDate === today && !o.isCorrection && isSettled(o)) {
      n++;
      if (o.reservationId) settledResIds.add(o.reservationId);   // S117
    }
  }
  for (const r of (state.reservations || [])) {
    // S117 — Reservierung mit verknüpfter settled Order zählt bereits über den
    // Order-Zweig (DB-Trigger tg_orders_complete_reservation_on_paid setzt sie
    // beim Bezahlen auf completed → sonst Doppelzähler). Order = Single-Source.
    if (settledResIds.has(r.id)) continue;               // S117
    if (r.date === today && isResDone(r)) n++;
  }
  return n;
}

/** Minuten seit Mitternacht (lokale Uhrzeit). */
function _nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

/** Relativ-Zeit-Label („in N Min" / überfällig „+N Min"). */
function _relTime(timeStr, nowMin) {
  if (!timeStr || nowMin == null) return { text: '', overdue: false };
  const m = timeToMinutes(timeStr);
  if (!Number.isFinite(m)) return { text: '', overdue: false };
  const diff = m - nowMin;
  const overdue = diff < 0;
  const abs = Math.abs(diff);
  let text;
  if (abs < 1) text = overdue ? 'jetzt fällig' : 'jetzt';
  else if (abs < 60) text = (overdue ? '+' : 'in ') + abs + ' Min';
  else {
    const h = Math.floor(abs / 60), mm = abs % 60;
    const hm = h + 'h' + (mm ? ' ' + mm + 'm' : '');
    text = (overdue ? '+' : 'in ') + hm;
  }
  return { text, overdue };
}

export function _renderTimelineRow(e, nowMin) {
  const stage = timelineStage(e, nowMin);
  const rel = _relTime(e.time, nowMin);
  const t1 = `${e.typeLabel} · ${e.title}`;
  const dot = stage.state === 'live' ? '<span class="today-tl-dot" aria-hidden="true"></span>' : '';
  // S108 — Reservierungs-Zeile öffnet das Detail-Modal direkt (statt in die
  // abgeschaffte Listen-Sicht zu springen); Bestellungen springen weiter in den
  // operativen Bestellungen-Tab. CSP: nur `func()`, kein `window.func()`.
  const onclick = e.kind === 'reservation'
    ? `openReservationDetail('${htmlEscape(e.id)}')`
    : `switchTab('${e.tab}')`;
  return `
    <button type="button" class="today-tl-row is-${stage.state}" onclick="${onclick}"
            title="${htmlEscape(e.typeLabel)} öffnen">
      <span class="today-tl-timecol">
        <span class="today-tl-time">${e.time || '—'}</span>
        ${rel.text ? `<span class="today-tl-rel${rel.overdue ? ' overdue' : ''}">${htmlEscape(rel.text)}</span>` : ''}
      </span>
      <span class="today-tl-icon" aria-hidden="true">${e.icon}</span>
      <span class="today-tl-main">
        <span class="today-tl-t1">${htmlEscape(t1)}${e.hasOrder ? `<span class="today-tl-orderbadge" title="Bestellung zu dieser Reservierung">🍳 Bestellung${e.orderNumber ? ' #' + htmlEscape(e.orderNumber) : ''}</span>` : ''}</span>
        ${e.detail ? `<span class="today-tl-t2">${htmlEscape(e.detail)}</span>` : ''}
      </span>
      <span class="today-tl-pill is-${stage.state}">${dot}${htmlEscape(stage.label)}</span>
    </button>`;
}

const _TIMELINE_DEPS = {
  isOrderFinished,
  isOrderCashedOut,   // S115 — kassiert (picked_up & paid) → Reservierung verschwindet
  isOrderPaid,        // S115 — speist die „Kassieren"-Pill (picked_up-unbezahlt)
  isOrderSettled,     // S115 — countTodayDone: erst kassiert/storniert zählt
  // S116 — Single-Source `isReservationDone` (capacity.js). Killt die alte Drift
  // `!isActiveReservation && !pending` (die `completed` NICHT als done erkannte,
  // weil isActiveReservation('completed')===true für die Kapazität gewollt ist).
  reservationDone: isReservationDone,
};

// S109 — Welche Sicht zeigt die kombinierte „Als Nächstes / Anfragen"-Karte?
// 'timeline' = chronologische Heute-Übersicht (Default), 'requests' = alle
// offenen Reservierungsanfragen (alle Tage). Modul-State überlebt Re-Renders.
let _nextPaneView = 'timeline';

/**
 * S109 — Kombinierte Karte mit Segmented-Toggle „Als Nächstes ⇄ Anfragen".
 * Statt zweier gestapelter Karten schaltet der Operator **in derselben Karte**
 * zwischen der chronologischen Heute-Timeline (`buildTodayTimeline`, heute-only)
 * und der vollständigen Anfragen-Liste (alle pending, alle Tage) um. Beide Zähler
 * stehen am Toggle. `viewOverride` ist nur für Tests (sonst Modul-State).
 */
export function renderNextPane(today, viewOverride) {
  const state = getState();
  const entries = buildTodayTimeline(state, today, _TIMELINE_DEPS);
  const doneCount = countTodayDone(state, today, _TIMELINE_DEPS);
  const nowMin = _nowMinutes();
  const pending = (state.reservations || []).filter(r => r.status === 'pending');
  // S192 — offene Bestellungen (Kunde gesendet, noch nicht angenommen) leben jetzt
  // im „Anfragen"-Reiter (statt separater Karte + Bulk). Je einzeln annehmen/ablehnen.
  const pendingOrders = (state.orders || []).filter(o => o.status === 'pending');
  const view = (viewOverride === 'requests' || viewOverride === 'timeline') ? viewOverride : _nextPaneView;
  ensureTimelineTick();

  const reqCount = pending.length + pendingOrders.length;
  const tlBadge  = entries.length ? ` <span class="dash-card-count">${entries.length}</span>` : '';
  const reqBadge = reqCount ? ` <span class="dash-card-count">${reqCount}</span>` : '';
  const toggle = `
    <div class="next-pane-toggle" role="tablist" aria-label="Als Nächstes / Anfragen">
      <button type="button" class="next-pane-tab ${view === 'timeline' ? 'active' : ''}"
              onclick="dashboardSetNextView('timeline')" role="tab" aria-selected="${view === 'timeline'}">
        Als Nächstes${tlBadge}
      </button>
      <button type="button" class="next-pane-tab ${view === 'requests' ? 'active' : ''}"
              onclick="dashboardSetNextView('requests')" role="tab" aria-selected="${view === 'requests'}">
        Anfragen${reqBadge}
      </button>
    </div>`;

  const body = view === 'requests'
    ? renderRequestsBody(pending, pendingOrders)
    : (entries.length === 0
        ? `<div class="empty">Keine offenen Aufgaben.</div>`
        : `<div class="today-tl-list">${entries.map(e => _renderTimelineRow(e, nowMin)).join('')}</div>`);

  return `
    <div class="card" id="dashboard-next-pane" style="margin-top: 20px;">
      <div class="dash-card-head next-pane-head">
        ${toggle}
        ${view === 'timeline' && doneCount > 0 ? `<span class="today-tl-donecount">✓ ${doneCount} heute erledigt</span>` : ''}
      </div>
      ${body}
    </div>`;
}

/** S109 — Toggle-Handler: schaltet die kombinierte Karte um + re-rendert nur
 *  diese Karte (non-destruktiv, Memory `gg-non-destructive-render`). CSP: func(). */
export function dashboardSetNextView(view) {
  _nextPaneView = (view === 'requests') ? 'requests' : 'timeline';
  const card = document.getElementById('dashboard-next-pane');
  if (card) card.outerHTML = renderNextPane(todayISO());
  else requestDashboardRerender();
}

// — S97/S109: 60-Sekunden-Tick für die Relativ-Zeit ——————————————————————
// Aktualisiert nur die „in N Min"/„+N Min"-Anzeige der Timeline-Sicht, ohne dass
// ein Realtime-Event nötig ist. Re-rendert nur die kombinierte Karte (nicht das
// ganze Dashboard) und clears sich selbst, sobald sie nicht mehr im DOM hängt.
let _timelineTick = null;
function ensureTimelineTick() {
  if (_timelineTick || typeof setInterval !== 'function') return;
  _timelineTick = setInterval(() => {
    const card = document.getElementById('dashboard-next-pane');
    if (!card) { clearInterval(_timelineTick); _timelineTick = null; return; }
    // Tab versteckt? Dann diesen Tick überspringen (Interval bleibt billig stehen).
    if (card.offsetParent === null) return;
    // Relativ-Zeiten sind nur in der Timeline-Sicht relevant.
    if (_nextPaneView !== 'timeline') return;
    card.outerHTML = renderNextPane(todayISO());
  }, 60000);
}

// Backward-Compat — der Toggle-Handler ist window-gebunden (Inline-onclick).
if (typeof window !== 'undefined') {
  Object.assign(window, { dashboardSetNextView });
}
